/*
 * DD AdminUI
 * Copyright (C) Fraunhofer IESE 2019 - 2025 Stefan Schweitzer, Shyalan Ramesh, Rafael Aranda López King,
 * Florian Wege, Johannes Schneider, Kazi Rezoanur Rahman, Christoph Müller, Pedram Jokar,
 * Mher Ter-Tovmasyan, Ben Burkhard, Matthias Gerbershagen, Anna Kleiner, Muhammad Tariq,
 * Adeline Silva Schäfer
 *
 * SPDX-License-Identifier: Apache-2.0
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Action, applyMiddleware, combineReducers, compose, createStore, Store } from 'redux';
import globalReducer from './Global.reducer';
import { snackbarNotificationReducer } from '../../src/shared/views/SnackbarNotification/store/SnackbarNotification.reducer';
import userReducer from '../modules/users/store/User.reducer';
import pushEventReducer from '../modules/push-events/store/PushEvent.reducer';
import flaggedContentReducer from '../modules/flagged-contents/store/FlaggedContent.reducer';
import groupReducer from '../modules/groups/store/Group.reducer';
import appConfigReducer from '../modules/app-configs/store/AppConfig.reducer';
import newsSourceReducer from '../modules/News-Sources/store/NewsSources.reducer';
import thunk, { ThunkDispatch } from 'redux-thunk';

const rootReducer = combineReducers({
	user: userReducer,
	pushEvent: pushEventReducer,
	flaggedContent: flaggedContentReducer,
	group: groupReducer,
	appConfig: appConfigReducer,
	newsSource: newsSourceReducer,
	snackbar: snackbarNotificationReducer,
	global: globalReducer
});

/**
 * The __REDUX_DEVTOOLS_EXTENSION_COMPOSE__ enhancer allows us to activate the redux devtools extension.
 * If the extension is not installed, we use the Redux compose.
 * More info in: https://github.com/zalmoxisus/redux-devtools-extension.
 */
// The __REDUX_DEVTOOLS_EXTENSION_COMPOSE__ property is only there if the extension is installed.
// Hence, it is not known by the default Window type.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const composeEnhancers = (window && (window as any).__REDUX_DEVTOOLS_EXTENSION_COMPOSE__) || compose;

export type PlatformAdminUIState = ReturnType<typeof rootReducer>;

export type PlatformAdminUIThunkType<T extends Action> = ThunkDispatch<PlatformAdminUIState, undefined, T>;

export default function configureStore(): Store {
	const store = createStore(rootReducer, composeEnhancers(applyMiddleware(thunk)));
	return store;
}

export const store = configureStore();
